package co.maytes.standupbot.dashboard.features.onboarding

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.integration.GithubRepoDto
import co.maytes.standupbot.shared.dto.integration.GithubReposResponseDto
import co.maytes.standupbot.shared.dto.integration.GithubWatchedReposDto
import co.maytes.standupbot.shared.dto.integration.IntegrationDto
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.integration.IntegrationStatusDto
import co.maytes.standupbot.shared.dto.integration.SyncMembersResponseDto
import co.maytes.standupbot.shared.dto.integration.UpdateGithubWatchedReposRequestDto

sealed interface IntegrationsLoadState {
    data object Idle : IntegrationsLoadState
    data object Loading : IntegrationsLoadState
    data object Ready : IntegrationsLoadState
    data class Error(val error: ApiError) : IntegrationsLoadState
}

sealed interface WatchedReposLoadState {
    data object Idle : WatchedReposLoadState
    data object Loading : WatchedReposLoadState
    data object Ready : WatchedReposLoadState
    data class Error(val error: ApiError) : WatchedReposLoadState
}

class IntegrationsStore(private val api: ApiClient) {
    var integrations: List<IntegrationDto> by mutableStateOf(emptyList())
        private set

    var loadState: IntegrationsLoadState by mutableStateOf(IntegrationsLoadState.Idle)
        private set

    var lastError: String? by mutableStateOf(null)
        private set

    suspend fun refresh(workspaceId: String) {
        if (loadState !is IntegrationsLoadState.Ready) {
            loadState = IntegrationsLoadState.Loading
        }
        when (val res = api.get<List<IntegrationDto>>("/api/v1/workspaces/$workspaceId/integrations")) {
            is Result.Ok -> {
                integrations = res.value.filter { it.provider != IntegrationProviderDto.UNKNOWN }
                lastError = null
                loadState = IntegrationsLoadState.Ready
            }
            is Result.Err -> {
                lastError = res.error.displayMessage
                loadState = IntegrationsLoadState.Error(res.error)
            }
        }
    }

    fun isConnected(provider: IntegrationProviderDto): Boolean =
        integrations.any { it.provider == provider && it.status == IntegrationStatusDto.CONNECTED }

    fun statusOf(provider: IntegrationProviderDto): IntegrationDto? =
        integrations.firstOrNull { it.provider == provider }

    suspend fun disconnect(
        workspaceId: String,
        provider: IntegrationProviderDto,
    ): Result<Unit, ApiError> {
        val slug = when (provider) {
            IntegrationProviderDto.SLACK -> "slack"
            IntegrationProviderDto.LINEAR -> "linear"
            IntegrationProviderDto.GOOGLE_CALENDAR -> "google"
            IntegrationProviderDto.GITHUB -> "github"
            IntegrationProviderDto.ANTHROPIC, IntegrationProviderDto.UNKNOWN ->
                return Result.Err(ApiError.Network("This integration can't be disconnected here"))
        }
        val res = api.delete<Unit>("/api/v1/workspaces/$workspaceId/integrations/$slug")
        if (res is Result.Ok) {
            refresh(workspaceId)
        }
        return res
    }

    suspend fun syncMembers(workspaceId: String): Result<SyncMembersResponseDto, ApiError> =
        api.post<Unit, SyncMembersResponseDto>(
            path = "/api/v1/workspaces/$workspaceId/integrations/sync-members",
            body = Unit,
        )

    var watchedReposState: WatchedReposLoadState by mutableStateOf(WatchedReposLoadState.Idle)
        private set

    var availableRepos: List<GithubRepoDto> by mutableStateOf(emptyList())
        private set

    var watchedRepos: Set<String> by mutableStateOf(emptySet())
        private set

    suspend fun loadGithubRepos(workspaceId: String) {
        if (watchedReposState !is WatchedReposLoadState.Ready) {
            watchedReposState = WatchedReposLoadState.Loading
        }
        val available = api.get<GithubReposResponseDto>(
            "/api/v1/workspaces/$workspaceId/integrations/github/repos",
        )
        val watched = api.get<GithubWatchedReposDto>(
            "/api/v1/workspaces/$workspaceId/integrations/github/watched-repos",
        )
        when {
            available is Result.Err ->
                watchedReposState = WatchedReposLoadState.Error(available.error)
            watched is Result.Err ->
                watchedReposState = WatchedReposLoadState.Error(watched.error)
            available is Result.Ok && watched is Result.Ok -> {
                availableRepos = available.value.repos
                watchedRepos = watched.value.repos.toSet()
                watchedReposState = WatchedReposLoadState.Ready
            }
        }
    }

    suspend fun saveWatchedRepos(
        workspaceId: String,
        repos: Set<String>,
    ): Result<Set<String>, ApiError> {
        val body = UpdateGithubWatchedReposRequestDto(repos = repos.toList().sorted())
        return when (
            val res = api.put<UpdateGithubWatchedReposRequestDto, GithubWatchedReposDto>(
                path = "/api/v1/workspaces/$workspaceId/integrations/github/watched-repos",
                body = body,
            )
        ) {
            is Result.Ok -> {
                watchedRepos = res.value.repos.toSet()
                Result.Ok(watchedRepos)
            }
            is Result.Err -> Result.Err(res.error)
        }
    }
}
